// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// The findings list's header row: the title, how many are still open, the
// view controls and the type filter. Presentational; the list owns the state.
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  type ReviewListGroupByControl,
  type ReviewListPinGrouping,
  type ReviewListShowMinorControl,
} from "./AskV2ReviewFilterMenuParts";
import {
  AskV2ReviewStatusTabs,
  type AskV2ReviewStatusTabsProps,
} from "./AskV2ReviewStatusTabs";
import {
  AskV2ReviewTagFilter,
  type ReviewSourceFilterOption,
} from "./AskV2ReviewTagFilter";
import {
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
} from "./reviewCommentTags";
import { type ReviewListStatusFilter } from "./useReviewListView";

export type AskV2ReviewSourceFilter = {
  sources: readonly ReviewSourceFilterOption[];
  hidden: ReadonlySet<ReviewCommentSourceKey>;
  onToggle: (source: ReviewCommentSourceKey, checked: boolean) => void;
};

export function AskV2ReviewCommentsHeader({
  unresolvedCount,
  showSplit,
  controls,
  showTagFilter,
  activeTags,
  alwaysOn,
  onToggleTag,
  onToggleAlwaysOn,
  sourceFilter,
  statusTabs,
  groupBy,
  pinGrouping,
  statusFilter,
  showMinor,
  onResetFilter,
  selectAll,
}: {
  unresolvedCount: number;
  /** The "N unresolved" line; off while the list is empty. */
  showSplit: boolean;
  /** Beside the filter; left out once that is the one Filter menu. */
  controls?: ReactNode;
  showTagFilter: boolean;
  activeTags: ReadonlySet<ReviewCommentTagId>;
  alwaysOn: boolean;
  onToggleTag: (tag: ReviewCommentTagId, checked: boolean) => void;
  onToggleAlwaysOn: (checked: boolean) => void;
  sourceFilter?: AskV2ReviewSourceFilter;
  /** To decide / All; drawn in place of the title and its unresolved count. */
  statusTabs?: AskV2ReviewStatusTabsProps;
  groupBy?: ReviewListGroupByControl;
  /** The pin view's block and place groups, offered in the menu's Group by. */
  pinGrouping?: ReviewListPinGrouping;
  /** The Status boxes; given, the type filter becomes the one Filter menu. */
  statusFilter?: ReviewListStatusFilter;
  showMinor?: ReviewListShowMinorControl;
  /** Resets the type, Always-on and raiser boxes; the menu resets the rest. */
  onResetFilter?: () => void;
  /** The bulk Select all box, drawn left of the tabs. */
  selectAll?: ReactNode;
}) {
  const { t } = useTranslation("ui");
  const resetFilter = () => {
    onResetFilter?.();
    statusFilter?.onReset();
    showMinor?.onChange(true);
  };
  // why: under the status tabs the pin view's groups are options in the one
  // Filter menu's Group by, so its own switch and View menu are not drawn.
  const inMenu = showTagFilter && statusFilter != null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
      {statusTabs ? (
        <div className="flex items-center gap-3">
          {selectAll}
          <AskV2ReviewStatusTabs {...statusTabs} />
        </div>
      ) : (
        // why: the split sits on the title's line, so a re-review's header is
        // as tall as a first review's and the cards stay level with the
        // artwork pane beside it.
        <div
          className="flex min-w-0 items-baseline gap-2"
          data-testid="ask-v2-review-findings-title-row"
        >
          <h2
            className="shrink-0 text-[13px] font-bold text-puntt-neutral-gray-12"
            data-testid="ask-v2-review-findings-title"
          >
            {t("askV2.review.findingsTitle")}
          </h2>
          {showSplit ? (
            // why: the header names what is still open, so a review's total
            // never reads as that many findings left to act on.
            <p
              className="m-0 truncate text-[12px] text-puntt-neutral-gray-10"
              data-testid="ask-v2-review-findings-split"
            >
              {t("askV2.review.findingsSplitUnresolved", {
                count: unresolvedCount,
              })}
            </p>
          ) : null}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {inMenu ? null : controls}
        {showTagFilter ? (
          <AskV2ReviewTagFilter
            activeTags={activeTags}
            alwaysOn={alwaysOn}
            onToggle={onToggleTag}
            onToggleAlwaysOn={onToggleAlwaysOn}
            sources={sourceFilter?.sources}
            hiddenSources={sourceFilter?.hidden}
            onToggleSource={sourceFilter?.onToggle}
            groupBy={groupBy}
            statusFilter={statusFilter}
            showMinor={showMinor}
            onReset={resetFilter}
            pinGrouping={inMenu ? pinGrouping : undefined}
          />
        ) : null}
      </div>
    </div>
  );
}
